// AppShell — sidebar + topbar + outlet.
//
// Renders only for authenticated users. Mobile (<768px): sidebar hides
// behind a hamburger, topbar shows the menu button. The mobile drawer
// is a Radix Dialog under the hood so it inherits focus trap, Esc
// handling, return-focus, and proper aria-modal semantics for free.

import { useState, type ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';
import {
  BANNER_HEIGHT,
  TestModeBanner,
  useShowTestBanner,
} from './test-mode-banner';
import { cn } from '@/lib/cn';

export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Reserve top-padding equal to the banner height when test mode is
  // active, so the topbar + sidebar don't sit under the fixed banner.
  const showBanner = useShowTestBanner();
  const bannerOffset = showBanner ? BANNER_HEIGHT : 0;

  return (
    <div
      className="flex h-full"
      style={{ paddingTop: bannerOffset, transition: 'padding-top 160ms ease' }}
    >
      <TestModeBanner />
      {/* Skip-to-main link — visible only when focused. Lets keyboard users
          jump past the sidebar nav (~20 items) on first Tab. */}
      <a
        href="#main-content"
        className={cn(
          'sr-only focus:not-sr-only',
          'focus:fixed focus:top-3 focus:left-3 focus:z-[200]',
          'focus:rounded-md focus:bg-[var(--color-ink)] focus:text-[var(--color-cream-50)]',
          'focus:px-3 focus:py-2 focus:text-caption focus:font-semibold',
          'focus:shadow-[var(--shadow-popover)] focus:outline-none',
        )}
      >
        Skip to main content
      </a>

      {/* Desktop sidebar. */}
      <div className="hidden md:block flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile sidebar drawer — Radix Dialog gives us focus trap,
          Esc handler, return-focus, aria-modal, and inert-on-body. */}
      <RadixDialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <RadixDialog.Portal>
          <RadixDialog.Overlay
            className="fixed inset-0 z-40 bg-[rgba(30,27,24,0.40)] md:hidden"
          />
          <RadixDialog.Content
            className="fixed inset-y-0 left-0 z-50 md:hidden outline-none"
            aria-label="Navigation"
          >
            <RadixDialog.Title className="sr-only">Navigation</RadixDialog.Title>
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </RadixDialog.Content>
        </RadixDialog.Portal>
      </RadixDialog.Root>

      <div className="flex flex-col flex-1 min-w-0">
        <Topbar onToggleSidebar={() => setMobileOpen((v) => !v)} />
        <main
          id="main-content"
          tabIndex={-1}
          className={cn(
            'flex-1 overflow-y-auto scroll-warm',
            'px-6 md:px-8 py-8',
          )}
        >
          <div className="mx-auto w-full" style={{ maxWidth: 'var(--content-max, 1280px)' }}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
